// ./src/utils/analytics/providers/AppInsightsProvider.ts
//
// ROADMAP #189 — Application Insights behind the same `analytics` calls as Google's: a page view is the ROUTE (urls hold record
// ids), and the events (docs/ANALYTICS-EVENTS.md) arrive beside the errors and the API calls of the same session. The rules
// (consent, what is scrubbed) are `@em/utils/browserTelemetry`'s.
import { startBrowserTelemetry, type BrowserTelemetry } from '@em/utils/browserTelemetry';

import { AnalyticsEventParams, AnalyticsProvider } from '../types';

/** App Insights properties are flat: an event's `items` list stays in GA. */
const flatParams = (params?: AnalyticsEventParams): Record<string, string | number | boolean> => {
  const flat: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(params ?? {})) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      flat[key] = value;
    }
  }

  return flat;
};

export class AppInsightsProvider implements AnalyticsProvider {
  name = 'app-insights';
  private telemetry: BrowserTelemetry | null = null;

  constructor(
    private connectionString: string,
    private apiHosts: string[],
  ) {}

  initialize(): void {
    this.telemetry ??= startBrowserTelemetry({ connectionString: this.connectionString, role: 'eventsmagic-app', apiHosts: this.apiHosts, correlate: true });
  }

  pageView(pageUrl: string, pageTitle: string, routePattern?: string | null): void {
    this.telemetry?.trackPageView(routePattern || pageTitle, pageUrl);
  }

  sendEvent(eventName: string, params?: AnalyticsEventParams): void {
    this.telemetry?.trackEvent(eventName, flatParams(params));
  }

  sendEventThen(eventName: string, params: AnalyticsEventParams | undefined, done: () => void): void {
    this.sendEvent(eventName, params);
    done();
  }

  setUser(): void {
    // ⓘ No user id: only the browser's error and its server trace are tied together.
  }
}
